import type { PortalProfile } from './generated/types.gen';

/**
 * A very small publish/subscribe helper for sign-out events.
 *
 * The API layer sits below React, so it cannot navigate or show a message by
 * itself. When it decides the session is over it announces that here, and the
 * React side reacts (clears cached data, sends the user to the sign-in screen).
 */

/**
 * Why a session ended. `manual` is Log Out; the rest are decided by the API
 * layer from the server's reply (portal-session-rules, design Domain model):
 * the backend's `data.reason` gives `signed-in-elsewhere`, `password-changed`
 * or `revoked`; a refused renewal gives `expired`; an account that is no longer
 * active gives `inactive`; a person left in no organization gives
 * `no-organization`.
 */
export type SignedOutReason =
  | 'manual'
  | 'signed-in-elsewhere'
  | 'password-changed'
  | 'revoked'
  | 'expired'
  | 'inactive'
  | 'no-organization';

/**
 * The reason for a session the backend refused, from its `data.reason`.
 * Anything else - ENDED, a reason this build does not know, or none from a
 * backend that predates them - is the generic `revoked`.
 */
export function signedOutReasonFrom(
  backendReason: string | null | undefined,
): SignedOutReason {
  if (backendReason === 'SIGNED_IN_ELSEWHERE') return 'signed-in-elsewhere';
  if (backendReason === 'PASSWORD_CHANGED') return 'password-changed';
  return 'revoked';
}

/** How a session ended, besides why: `broadcast` is false when another tab told this one. */
export interface SignedOutHow {
  broadcast: boolean;
}

type Listener = (reason: SignedOutReason, how: SignedOutHow) => void;
type ProfileListener = (profile: PortalProfile) => void;

const listeners = new Set<Listener>();
const profileListeners = new Set<ProfileListener>();

export const authEvents = {
  /** Registers a listener. Returns a function that removes it again. */
  onSignedOut(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  emitSignedOut(reason: SignedOutReason, how: SignedOutHow = { broadcast: true }): void {
    for (const listener of listeners) {
      listener(reason, how);
    }
  },

  /**
   * The signed-in person as the server just described them, from a renewal
   * reply (portal-session-rules, B12), so a role change or a new
   * organization reaches the screen within one renewal.
   */
  onProfileUpdated(listener: ProfileListener): () => void {
    profileListeners.add(listener);
    return () => {
      profileListeners.delete(listener);
    };
  },

  emitProfileUpdated(profile: PortalProfile): void {
    for (const listener of profileListeners) {
      listener(profile);
    }
  },
};
